/* DORTI "spin the pick" viewer: markup in snippets/dorti-spin.liquid.
 *
 * OPEN. A tap on the first product photo. The viewer's grey (the photo's own)
 * fades in while each cut-out pick flies from exactly where it sits in the
 * photo to its place on screen and tilts, so the photo seems to open up
 * around it. Close reverses it. A pack photo has several picks; each one is
 * its own pick here, with its own spin.
 *
 * SPIN. Drag turns the pick under your finger (sideways all the way round, up
 * and down up to 70°). A flick keeps it spinning and slows it with friction,
 * then it settles on its front or back. A tap flips it. Left alone for a few
 * seconds it turns slowly on its own, like a shop turntable.
 *
 * Shading, sheen and the floor shadow follow the angle. The edge is as thick
 * as the chosen gauge. The sticky Add to cart bar stays up underneath (it
 * reads data-dorti-spin-open on <html>); the cart opening closes the viewer.
 *
 * ALWAYS A WAY OUT. The viewer lives directly in <body>, so nothing around it
 * can crop it, and it is fitted to the part of the page on screen: a shopper
 * who pinch-zoomed the photo first would otherwise get a viewer bigger than
 * the screen, with the close button off it and no way to pan to it.
 */
(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('dorti-spin')) return;

  const ease = (t) => 1 - Math.pow(1 - t, 3);
  const easeBack = (t) => {
    const c = 1.4;
    return 1 + (c + 1) * Math.pow(t - 1, 3) + c * Math.pow(t - 1, 2);
  };
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

  class DortiSpin extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.stage = this.querySelector('[data-spin-stage]');
      this.hint = this.querySelector('[data-spin-hint]');
      this.gaugeChip = this.querySelector('[data-spin-gauge]');
      this.closeBtn = this.querySelector('[data-spin-close]');
      this.still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.picks = Array.from(this.querySelectorAll('[data-spin-fly]')).map((fly, i) => ({
        i,
        fly,
        pick: fly.querySelector('[data-spin-pick]'),
        floor: fly.querySelector('[data-spin-floor]'),
        frac: (fly.dataset.frac || '0,0,1,1').split(',').map(Number),
        // the cut-out is upright; this turns it back to how it lies in the photo
        tilt: parseFloat(fly.dataset.tilt) || 0,
        rx: 0,
        ry: 0,
        v: 0,
        tween: null,
        idleSince: 0,
      }));
      this.active = null;
      this.zoom = 1;
      // the product this viewer belongs to, kept before it moves to <body>
      this.info = this.closest('product-info') || document;

      this.onDocClick = this.onDocClick.bind(this);
      this.onDown = this.onDown.bind(this);
      this.onMove = this.onMove.bind(this);
      this.onUp = this.onUp.bind(this);
      this.onKey = this.onKey.bind(this);
      this.frame = this.frame.bind(this);
      this.close = this.close.bind(this);
      this.warm = this.warm.bind(this);
      this.fitToScreen = this.fitToScreen.bind(this);

      document.addEventListener('click', this.onDocClick, true);
      this.stage.addEventListener('pointerdown', this.onDown);
      this.stage.addEventListener('pointermove', this.onMove);
      this.stage.addEventListener('pointerup', this.onUp);
      this.stage.addEventListener('pointercancel', this.onUp);
      this.closeBtn.addEventListener('click', () => this.close());
      // The wheel and stray touches never scroll the page behind
      this.addEventListener('wheel', (e) => e.preventDefault(), { passive: false });
      this.addEventListener('touchmove', (e) => e.preventDefault(), { passive: false });
      document.addEventListener('theme:cart-drawer:open', () => this.close(true));

      // The edge follows the thickness picked on the page. Clicks too: picking
      // the box that's ticked underneath while nothing is picked yet fires no change.
      const info = this.info;
      const regauge = () => requestAnimationFrame(() => this.updateGauge());
      info.addEventListener('change', regauge);
      info.addEventListener('click', (e) => {
        if (e.target.closest && e.target.closest('variant-selects')) regauge();
      });
      this.updateGauge();

      this.markPhoto();
      this.warmLater();

      // Out of the product section, so no transform or overflow there can crop it
      // (moving it runs connectedCallback again, which returns at once)
      if (this.parentElement !== document.body) document.body.appendChild(this);
    }

    /* ---------- the first photo ---------- */

    firstSlide() {
      return this.info.querySelector('.product__slides .product__slide');
    }

    firstImage() {
      const slide = this.firstSlide();
      return slide && slide.querySelector('img');
    }

    // A small "Tap to spin" chip on the first photo, so the way in is visible
    markPhoto() {
      const slide = this.firstSlide();
      const holder = slide && (slide.querySelector('.product__photo') || slide);
      if (!holder || holder.querySelector('.dorti-spin-chip')) return;
      if (getComputedStyle(holder).position === 'static') holder.style.position = 'relative';
      const chip = document.createElement('span');
      chip.className = 'dorti-spin-chip';
      chip.setAttribute('aria-hidden', 'true');
      chip.innerHTML = '<svg viewBox="0 0 24 24" focusable="false"><path d="M20 12a8 8 0 1 1-2.4-5.7"/><path d="M20 4v4h-4"/></svg>Tap to spin';
      holder.appendChild(chip);
      // a double tap on the photo would zoom the page as the viewer opens
      slide.style.touchAction = 'manipulation';
      // a finger on the photo starts the download if the page hasn't yet
      slide.addEventListener('pointerdown', this.warm, { once: true, passive: true });
    }

    // The cut-outs load once the page has, so they never hold up its own photos
    warmLater() {
      const go = () => {
        if (window.requestIdleCallback) window.requestIdleCallback(this.warm, { timeout: 2500 });
        else setTimeout(this.warm, 1200);
      };
      if (document.readyState === 'complete') go();
      else window.addEventListener('load', go, { once: true });
    }

    warm() {
      if (this.warmed) return;
      this.warmed = true;
      this.querySelectorAll('img[data-src]').forEach((img) => {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      });
      // the edge masks too, so they're cached before the first open
      this.picks.forEach((p) => {
        if (p.fly.dataset.edgeSrc) new Image().src = p.fly.dataset.edgeSrc;
      });
    }

    onDocClick(event) {
      if (this.isOpen) return;
      const slide = this.firstSlide();
      if (!slide || !event.target.closest || !slide.contains(event.target)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.open();
    }

    // Where a pick sits on screen inside the photo right now
    photoRect(p) {
      const img = this.firstImage();
      if (!img) return null;
      const r = img.getBoundingClientRect();
      if (!r.width) return null;
      const [fx, fy, fw, fh] = p.frac;
      return { x: r.left + fx * r.width, y: r.top + fy * r.height, w: fw * r.width, h: fh * r.height };
    }

    // The transform that puts a pick back over its place in the photo
    flightFromPhoto(p) {
      const from = this.photoRect(p);
      if (!from) return 'translateY(40px) scale(0.6)';
      const prev = p.fly.style.transform;
      p.fly.style.transform = 'none';
      const to = p.fly.getBoundingClientRect();
      p.fly.style.transform = prev;
      const s = from.w / to.width;
      // screen distances, in the viewer's own (unzoomed) pixels
      const dx = (from.x + from.w / 2 - (to.left + to.width / 2)) * this.zoom;
      const dy = (from.y + from.h / 2 - (to.top + to.height / 2)) * this.zoom;
      return `translate(${dx}px, ${dy}px) scale(${s})`;
    }

    // Cover exactly what is on screen. Pinch-zoomed, the visible part of the
    // page is smaller than the page and offset; the viewer is laid out at full
    // size and scaled down onto it, so it looks the same as unzoomed.
    fitToScreen() {
      const vv = window.visualViewport;
      const fitted = vv && (Math.abs(vv.scale - 1) > 0.01 || vv.offsetTop > 0.5 || vv.offsetLeft > 0.5);
      this.zoom = fitted ? vv.scale : 1;
      if (!fitted) {
        ['inset', 'width', 'height', 'transform', 'transformOrigin'].forEach((prop) => { this.style[prop] = ''; });
        return;
      }
      this.style.inset = '0 auto auto 0';
      this.style.width = vv.width * vv.scale + 'px';
      this.style.height = vv.height * vv.scale + 'px';
      this.style.transformOrigin = '0 0';
      this.style.transform = `translate(${vv.offsetLeft}px, ${vv.offsetTop}px) scale(${1 / vv.scale})`;
    }

    /* ---------- thickness ---------- */

    // The gauge chosen on the page ("Thin (0.60mm)"), or the product's own figure
    updateGauge() {
      let mm = 0;
      let label = '';
      const chosen = this.info.querySelectorAll('variant-selects input:checked, variant-selects select');
      for (const input of chosen) {
        if (input.closest('.is-unpicked')) continue;
        const m = /(\d+(?:\.\d+)?)\s*mm/i.exec(input.value || '');
        if (m) {
          mm = parseFloat(m[1]);
          label = m[1] + 'mm';
          break;
        }
      }
      if (!mm) {
        // "0.96mm", or a range ("0.60–1.00mm") read at its middle
        const all = (this.dataset.gauge || '').match(/\d+(?:\.\d+)?/g) || [];
        const nums = all.map(Number).filter((n) => n > 0 && n < 3);
        if (nums.length) mm = (Math.min(...nums) + Math.max(...nums)) / 2;
        label = this.dataset.gauge || '';
      }
      if (this.gaugeChip && label) this.gaugeChip.textContent = label;
      if (!mm) return;
      // a touch thicker than life so the edge reads on a phone, and never a hairline
      const width = parseFloat(this.dataset.widthMm) || 27;
      this.style.setProperty('--thick', clamp((mm / width) * 1.1, 0.018, 0.05).toFixed(4));
    }

    /* ---------- open / close ---------- */

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.warm();
      this.returnFocus = document.activeElement;
      this.hidden = false;
      this.fitToScreen();
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.fitToScreen);
        window.visualViewport.addEventListener('scroll', this.fitToScreen);
      }
      document.documentElement.setAttribute('data-dorti-spin-open', '');
      this.refreshBar();

      this.picks.forEach((p) => {
        p.rx = 0;
        p.ry = 0;
        p.v = 0;
        p.tween = null;
        this.render(p);
        p.fly.style.transition = 'none';
        p.fly.style.transform = this.flightFromPhoto(p);
      });
      const img = this.firstImage();
      if (img) img.style.visibility = 'hidden';

      // two frames so the start position is painted before the flight begins
      requestAnimationFrame(() => requestAnimationFrame(() => {
        this.classList.add('is-open');
        const now = performance.now();
        this.picks.forEach((p) => {
          const delay = p.i * 50;
          p.fly.style.transition = this.still ? 'none' : `transform 560ms cubic-bezier(0.2, 0.9, 0.25, 1) ${delay}ms`;
          p.fly.style.transform = 'none';
          // a small tilt on arrival says "this turns"; a pack's picks lean different ways
          const lean = this.picks.length > 1 && p.i % 2 ? -1 : 1;
          this.tweenTo(p, this.still ? 0 : -12, this.still ? 0 : 32 * lean, 700, ease, delay);
          p.idleSince = now;
        });
        this.running = true;
        this.lastFrame = now;
        requestAnimationFrame(this.frame);
        this.closeBtn.focus({ preventScroll: true });
      }));
      document.addEventListener('keydown', this.onKey);
    }

    close(instant) {
      if (!this.isOpen || this.closing) return;
      this.closing = true;
      document.removeEventListener('keydown', this.onKey);
      const finish = () => {
        this.running = false;
        this.closing = false;
        this.isOpen = false;
        this.hidden = true;
        this.classList.remove('is-open');
        this.picks.forEach((p) => {
          p.fly.style.transition = 'none';
          p.fly.style.transform = 'none';
        });
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', this.fitToScreen);
          window.visualViewport.removeEventListener('scroll', this.fitToScreen);
        }
        const img = this.firstImage();
        if (img) img.style.visibility = '';
        document.documentElement.removeAttribute('data-dorti-spin-open');
        this.refreshBar();
        if (!instant && this.returnFocus && this.returnFocus.focus) this.returnFocus.focus({ preventScroll: true });
      };
      if (instant || this.still) {
        finish();
        return;
      }
      // face the front again and fly back onto the photo
      this.active = null;
      this.picks.forEach((p) => {
        p.v = 0;
        this.tweenTo(p, 0, Math.round(p.ry / 360) * 360, 380, ease);
        p.fly.style.transition = 'transform 420ms cubic-bezier(0.4, 0, 0.2, 1)';
        p.fly.style.transform = this.flightFromPhoto(p);
      });
      this.classList.remove('is-open');
      setTimeout(finish, 430);
    }

    onKey(event) {
      if (event.key === 'Escape') this.close();
    }

    refreshBar() {
      const bar = document.querySelector('dorti-sticky-atc');
      if (bar && typeof bar.updateVisibility === 'function') bar.updateVisibility();
    }

    /* ---------- spin ---------- */

    // The pick under (or nearest) the finger
    pickAt(x, y) {
      if (this.picks.length === 1) return this.picks[0];
      let best = null;
      let bestD = Infinity;
      this.picks.forEach((p) => {
        const r = p.fly.getBoundingClientRect();
        const d = Math.hypot(x - (r.left + r.width / 2), y - (r.top + r.height / 2));
        if (d < bestD) {
          bestD = d;
          best = p;
        }
      });
      return best;
    }

    onDown(event) {
      if (event.button !== undefined && event.button > 0) return;
      const p = this.pickAt(event.clientX, event.clientY);
      if (!p) return;
      this.active = p;
      this.moved = 0;
      this.downAt = performance.now();
      this.lastX = event.clientX;
      this.lastY = event.clientY;
      this.lastT = this.downAt;
      p.v = 0;
      p.tween = null;
      this.stage.classList.add('is-dragging');
      if (this.stage.setPointerCapture) this.stage.setPointerCapture(event.pointerId);
      if (this.hint) this.hint.classList.add('is-done');
    }

    onMove(event) {
      const p = this.active;
      if (!p) return;
      const now = performance.now();
      // finger travel on the screen, the same at any page zoom
      const dx = (event.clientX - this.lastX) * this.zoom;
      const dy = (event.clientY - this.lastY) * this.zoom;
      const dt = Math.max(1, now - this.lastT);
      this.moved += Math.abs(dx) + Math.abs(dy);
      p.ry += dx * 0.6;
      p.rx = clamp(p.rx - dy * 0.4, -70, 70);
      // a smoothed degrees-per-millisecond, for the flick
      p.v = p.v * 0.5 + ((dx * 0.6) / dt) * 0.5;
      this.lastX = event.clientX;
      this.lastY = event.clientY;
      this.lastT = now;
      this.render(p);
    }

    onUp() {
      const p = this.active;
      if (!p) return;
      this.active = null;
      this.stage.classList.remove('is-dragging');
      const now = performance.now();
      p.idleSince = now;
      // A tap, not a drag: flip it over
      if (this.moved < 8 && now - this.downAt < 300) {
        p.v = 0;
        this.tweenTo(p, p.rx * 0.5, Math.round(p.ry / 180) * 180 + 180, 650, easeBack);
        return;
      }
      // A drag that stopped before letting go doesn't fling
      if (now - this.lastT > 80) p.v = 0;
      p.v = clamp(p.v, -2.6, 2.6);
    }

    tweenTo(p, rx, ry, duration, curve, delay) {
      p.tween = { fromX: p.rx, fromY: p.ry, toX: rx, toY: ry, start: performance.now() + (delay || 0), duration, curve };
    }

    frame(now) {
      if (!this.running) return;
      const dt = Math.min(48, now - this.lastFrame);
      this.lastFrame = now;
      this.picks.forEach((p) => {
        if (p === this.active) return;
        if (p.tween) {
          const t = clamp((now - p.tween.start) / p.tween.duration, 0, 1);
          const k = p.tween.curve(t);
          p.rx = p.tween.fromX + (p.tween.toX - p.tween.fromX) * k;
          p.ry = p.tween.fromY + (p.tween.toY - p.tween.fromY) * k;
          if (t >= 1) {
            p.tween = null;
            p.idleSince = now;
          }
        } else if (Math.abs(p.v) > 0.03) {
          // flung: friction, and the tilt drifts back towards level
          p.ry += p.v * dt;
          p.v *= Math.pow(0.955, dt / 16);
          p.rx += (-8 - p.rx) * 0.04;
          p.idleSince = now;
        } else if (p.v !== 0) {
          // slowed right down: settle on whichever face is nearer
          p.v = 0;
          this.tweenTo(p, -8, Math.round(p.ry / 180) * 180, 520, ease);
        } else if (!this.still && now - p.idleSince > 3200) {
          // left alone: turn slowly, like a shop turntable
          p.ry += 0.02 * dt;
          p.rx += (-10 - p.rx) * 0.02;
        }
        this.render(p);
      });
      requestAnimationFrame(this.frame);
    }

    render(p) {
      p.pick.style.transform = `rotateZ(${p.tilt}deg) rotateX(${p.rx.toFixed(2)}deg) rotateY(${p.ry.toFixed(2)}deg)`;
      const rad = (p.ry * Math.PI) / 180;
      const facing = Math.abs(Math.cos(rad)) * Math.cos((p.rx * Math.PI) / 180);
      p.pick.style.setProperty('--shade', ((1 - facing) * 0.45).toFixed(3));
      p.pick.style.setProperty('--shine', (50 + Math.sin(rad) * 70 - p.rx * 0.6).toFixed(1) + '%');
      p.floor.style.setProperty('--floor', (0.25 + 0.75 * Math.abs(Math.cos(rad))).toFixed(3));
      // Only the face turned towards the viewer is shown, decided here rather
      // than left to backface-visibility, which Safari doesn't always honour.
      const back = Math.cos(rad) < 0;
      if (p.back !== back) {
        p.back = back;
        p.pick.classList.toggle('is-back', back);
      }
    }
  }

  window.customElements.define('dorti-spin', DortiSpin);
})();
